import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, MailCheck, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const token = params.get("token");
  // "verifying" when a token is present; otherwise a prompt for logged-in unverified users.
  const [state, setState] = useState(token ? "loading" : "prompt");
  const [message, setMessage] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await api("/auth/verify-email", { method: "POST", body: JSON.stringify({ token }) });
        setState("ok");
        if (res.already_verified) setMessage("This email was already verified.");
        try { await refresh(); } catch { /* not logged in on this device — fine */ }
      } catch (e) {
        setState("error");
        setMessage(e.message || "This verification link is invalid or has expired.");
      }
    })();
  }, [token, refresh]);

  // If a logged-in user is already verified, don't keep them here.
  useEffect(() => {
    if (!token && user && user.email_verified !== false) navigate("/dashboard", { replace: true });
  }, [token, user, navigate]);

  const resend = async () => {
    setResending(true);
    try {
      const res = await api("/auth/resend-verification", { method: "POST" });
      if (res.already_verified) { toast.success("Already verified — redirecting"); await refresh(); navigate("/dashboard"); return; }
      toast.success(res.email_configured === false ? "Verification link generated (email activates once deployed)" : "Verification email sent — check your inbox");
    } catch (e) {
      toast.error(e.message || "Could not resend verification email");
    } finally { setResending(false); }
  };

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4" data-testid="verify-email-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
        {state === "loading" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-brand" />
            <p className="mt-4 text-sm text-muted-foreground">Verifying your email…</p>
          </>
        )}
        {state === "ok" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" data-testid="verify-email-success" />
            <h1 className="mt-4 text-xl font-bold">Email verified</h1>
            <p className="mt-2 text-sm text-muted-foreground">{message || "Your email address is confirmed. Your account is secured."}</p>
            <Link to="/dashboard" data-testid="verify-go-dashboard" className="mt-6 inline-block rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white">
              Go to dashboard
            </Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-red-500" data-testid="verify-email-error" />
            <h1 className="mt-4 text-xl font-bold">Verification failed</h1>
            <p className="mt-2 text-sm text-muted-foreground">{message}</p>
            {user ? (
              <button onClick={resend} disabled={resending} data-testid="verify-resend-btn" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailCheck className="h-4 w-4" />}Resend verification email
              </button>
            ) : (
              <Link to="/login" className="mt-6 inline-block rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white">Sign in to resend</Link>
            )}
          </>
        )}
        {state === "prompt" && (
          <>
            <MailCheck className="mx-auto h-12 w-12 text-brand" data-testid="verify-email-prompt" />
            <h1 className="mt-4 text-xl font-bold">Verify your email</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {user?.email ? <>We sent a verification link to <b className="text-midnight">{user.email}</b>.</> : "Please confirm your email address to access your workshop."}
              {" "}Click the link in that email, then return here. The link is valid for 24 hours.
            </p>
            <button onClick={resend} disabled={resending} data-testid="verify-resend-btn" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailCheck className="h-4 w-4" />}Resend verification email
            </button>
            <button onClick={doLogout} data-testid="verify-logout-btn" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-bold text-midnight">
              <LogOut className="h-4 w-4" />Log out
            </button>
          </>
        )}
      </div>
    </div>
  );
}
